import { filterClasses, warn } from "@swivl-mono/helpers";
import { APIScope, LocationPageInterface, LocationVarInheritance } from "@swivl-mono/types";
import { Button, ButtonColor, ImageUploader, ImageUploaderType } from "@swivl-mono/ui";
import { BiEdit } from "react-icons/bi";
import { useNavigate } from "react-router-dom";

import { FacilityVarSet, facilityVarKeysFor } from "../../../../Helpers/FeatureFlags/facilityVarKeys";
import { useHasPermission } from "../../../../Helpers/Permissions/useHasPermission";
import { useMergedLocationVariables } from "../../../../Hooks/useLocationVariables";
import LocationsModel from "../../../../Models/Locations/Locations.Model";
import LocationVarsModel from "../../../../Models/Variables/LocationVars.Model";
import "./Location.Details.Header.scss";

interface LocationDetailsHeaderProps extends LocationPageInterface {
  children: React.ReactNode;
  /**
   * Which facility-variable set the RENDERING PAGE lives on (ENG-3198 rule,
   * ENG-3299): the legacy Property Manager (details + cortex tabs) reads and
   * writes the legacy keys; Properties v3 (details-v3) the `Cortex*` keys.
   * Never pick by data presence or by viewer.
   */
  varSet: FacilityVarSet;
}

/**
 * Details v3 sub-pages that own their editing affordance and so don't want
 * the banner's page-level Edit/Done toggle: Escalation & Ticketing and
 * Emergency edit via flyouts, the rest via per-section Edit buttons. This
 * list is v3-only (ENG-3300): the legacy Property Manager reuses several of
 * these same slugs for tabs that have no per-section editing, so it always
 * shows the banner toggle regardless of this list.
 */
const SUBPAGES_WITHOUT_BANNER_EDIT = new Set([
  "escalation-and-ticketing",
  "call-scripts",
  "emergency",
  "facility-info",
  "amenities",
  "discounts",
  "urls",
  "website",
  "conversation-starters",
  "hours",
  "admin",
  "google-reviews",
  "sequences"
]);

export function LocationDetailsHeader(props: LocationDetailsHeaderProps) {
  const { groups, locations } = LocationsModel.useState();
  const canEditLocationDetails = useHasPermission(APIScope.LOCATION_DETAILS_WRITE, props.groupId, props.locationId);

  const keys = facilityVarKeysFor(props.varSet);
  const vars = useMergedLocationVariables(
    [keys.name, keys.imageURL, keys.address, keys.city, keys.state, keys.postalCode, keys.countryCode],
    props.groupId,
    props.locationId
  );

  const { location, group } = props;
  const navigate = useNavigate();
  const hideBannerEdit = props.varSet === "cortex" && SUBPAGES_WITHOUT_BANNER_EDIT.has(props.subPage ?? "");

  let name = "NAME";
  let subText = "";
  let subText2 = "";

  let infoContent: React.ReactNode = null;
  if (props.locationId) {
    if (location) {
      name = vars?.[keys.name]?.value || "";
      subText = vars?.[keys.address]?.value ? vars?.[keys.address]?.value + " " : "";
      subText2 += vars?.[keys.city]?.value ? vars?.[keys.city].value + ", " : "";
      subText2 += vars?.[keys.state]?.value ? vars?.[keys.state]?.value + " " : "";
      subText2 += vars?.[keys.postalCode]?.value ? vars?.[keys.postalCode]?.value + " " : "";
      subText2 += vars?.[keys.countryCode]?.value && vars?.[keys.countryCode]?.value !== "US" ? vars?.[keys.countryCode]?.value : "";
    }
  } else if (props.groupId) {
    if (group) {
      name = vars?.[keys.name]?.value || "";
      subText = group.locations?.length ? group.locations.length + " Locations" : "0 Locations";
      infoContent = (
        <div className="info">
          You're editing <b>group</b> settings. These will apply to all locations within this group. Customizations at the location level will remain
          intact.
        </div>
      );
    }
  } else {
    name = vars?.[keys.name]?.value || "";
    if (groups?.length) {
      subText += groups.length + " Groups \n";
    }
    if (locations?.length) {
      subText += locations.length + " Locations";
    }
    infoContent = (
      <div className="info">
        You're editing <b>global</b> settings across your entire organization. These will apply to all groups and locations. Customizations at the
        group or location level will remain intact.
      </div>
    );
  }

  return (
    <div className="LocationDetailsHeader">
      <div className="background">
        <img src={vars?.[keys.imageURL]?.value} alt={`Avatar for: ${name}`} />
      </div>
      <div className="heading">
        <ImageUploader
          isEditable={props.isEditing}
          text="Click to Upload"
          type={ImageUploaderType.LOCATION}
          imageURL={vars?.[keys.imageURL]?.value}
          imageUploaded={(url) => {
            const varId = vars?.[keys.imageURL]?.id;
            if (vars?.[keys.imageURL]?.inheritance === LocationVarInheritance.NONE) {
              if (!varId) {
                warn(`No varId found for ${keys.imageURL}`);
              }
              LocationVarsModel.saveVariable(varId!, url);
            }

            LocationVarsModel.createVariable(keys.imageURL, url, props.groupId && !props.locationId ? props.groupId : undefined, props.locationId);
          }}
        />

        <div className="titleSection">
          <h2>{name}</h2>
          {subText}
          <br />
          {subText2}
        </div>
        {canEditLocationDetails && !hideBannerEdit && (
          <div className="editButton">
            {props.isEditing ? (
              <Button
                color={ButtonColor.ORANGE}
                onClick={() => {
                  const url = `${props.preamble}/page/${props.page}${props.subPage ? "/" + props.subPage : ""}`;
                  navigate(url);
                }}
              >
                Done
              </Button>
            ) : (
              <Button
                color={ButtonColor.WHITE}
                onClick={() => {
                  const url = `${props.preamble}/page/${props.page}${props.subPage ? "/" + props.subPage : ""}/edit`;
                  navigate(url);
                }}
              >
                Edit <BiEdit />
              </Button>
            )}
          </div>
        )}
      </div>
      {/* Mirrors the worksheet content container (p-5 → mx-auto max-w-[960px] p-6)
          so the banner's edges line up with the card below it. Keep the max-width
          here in sync with CortexTopicWorksheet. */}
      {infoContent && (
        <div className="px-5">
          <div className="mx-auto max-w-[960px] px-6">{infoContent}</div>
        </div>
      )}
      {/* With a banner above it, the page container's own p-6 (24px) already
          supplies most of the gap, so the top padding drops to 8px for 32px
          total — matching the 32px above the banner. */}
      <div className={filterClasses("p-5", infoContent && "pt-2")}>{props.children}</div>
    </div>
  );
}
